import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import apiService from '../../services/api';
import AuthLayout from './AuthLayout';
import GoogleSignInButton from './GoogleSignInButton';

const LoginPage = ({ onLogin, onSwitchToSignup, onNeedsCompany }) => {
  const { t } = useTranslation();
  const [credentials, setCredentials] = useState({
    email: '',
    password: '',
    platform: 'universal',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const response = await apiService.login(credentials);
      const { token, user } = response.data;

      // Persist the shop/user so the dashboard can resolve the portal domain
      // even before the backend returns it in dashboard-stats.
      if (user) {
        localStorage.setItem('user', JSON.stringify(user));
      }

      onLogin(token, {
        planId: user.planId,
        isFreemium: user.isFreemium,
      });
    } catch (error) {
      setError(error.response?.data?.message || t('login_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    setCredentials({ ...credentials, [e.target.name]: e.target.value });
  };

  return (
    <AuthLayout
      title={t('welcome_back')}
      subtitle={t('sign_in_to_account')}
      footer={
        <p className="auth-switch">
          Don&apos;t have an account?{' '}
          <button type="button" onClick={onSwitchToSignup} className="auth-link">
            Sign up
          </button>
        </p>
      }
    >
      <form onSubmit={handleSubmit} className="auth-form">
        {error && (
          <div className="auth-error" role="alert">
            {error}
          </div>
        )}

        <GoogleSignInButton onLogin={onLogin} onError={setError} onNeedsCompany={onNeedsCompany} />

        <div className="auth-divider">
          <span>or continue with email</span>
        </div>

        <div className="auth-field">
          <label htmlFor="email" className="auth-label">
            Email address
          </label>
          <div className="auth-input-wrap">
            <Mail size={18} className="auth-input-icon" />
            <input
              id="email"
              name="email"
              type="email"
              required
              className="auth-input"
              placeholder="you@company.com"
              value={credentials.email}
              onChange={handleChange}
              autoComplete="email"
            />
          </div>
        </div>

        <div className="auth-field">
          <div className="auth-label-row">
            <label htmlFor="password" className="auth-label">
              Password
            </label>
            <a
              href="mailto:support@zonstaff.com?subject=Password%20reset%20request"
              className="auth-forgot"
            >
              Forgot password?
            </a>
          </div>
          <div className="auth-input-wrap">
            <Lock size={18} className="auth-input-icon" />
            <input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              required
              className="auth-input"
              placeholder="Enter your password"
              value={credentials.password}
              onChange={handleChange}
              autoComplete="current-password"
            />
            <button
              type="button"
              className="auth-input-toggle"
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>
        </div>

        <button type="submit" className="auth-submit" disabled={isLoading}>
          {isLoading ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default LoginPage;
